import React from 'react';
import { Scale, Globe, Users, Medal, Landmark, ClipboardCheck, Briefcase, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';

const WhyUsPage = () => {
  const reasons = [
    {
      icon: <Scale className="w-8 h-8 text-[#D4A74A]" strokeWidth={2} />,
      title: 'Deep Experience in Thai Legal System',
      description: 'Our attorneys possess extensive knowledge and years of practical experience navigating the complexities of Thai law, ensuring expert guidance for your case.'
    },
    {
      icon: <ClipboardCheck className="w-8 h-8 text-[#D4A74A]" strokeWidth={2} />,
      title: 'Wide Range of Legal Services',
      description: 'From visa applications to criminal defence, property law to family matters, we offer comprehensive legal services under one roof in Pattaya.'
    },
    {
      icon: <Globe className="w-8 h-8 text-[#D4A74A]" strokeWidth={2} />,
      title: 'Support for Foreign & Domestic Clients',
      description: 'We specialise in serving both Thai nationals and international expats, with full English-language support and cultural understanding.'
    },
    {
      icon: <Briefcase className="w-8 h-8 text-[#D4A74A]" strokeWidth={2} />,
      title: 'Professional Handling to Reduce Hassle',
      description: 'We manage the complex legal procedures and paperwork, allowing you to focus on what matters most whilst we handle the details.'
    },
    {
      icon: <Users className="w-8 h-8 text-[#D4A74A]" strokeWidth={2} />,
      title: 'Longstanding Presence & Reputation',
      description: 'As a mainstay on the Pattaya legal scene for many years, our reputation is built on consistent results and client satisfaction.'
    },
    {
      icon: <Medal className="w-8 h-8 text-[#D4A74A]" strokeWidth={2} />,
      title: 'Proper Registration & Legitimacy',
      description: 'Fully registered with the Lawyer\'s Council of Thailand, ensuring all services meet the highest professional and ethical standards.'
    },
    {
      icon: <Landmark className="w-8 h-8 text-[#D4A74A]" strokeWidth={2} />,
      title: 'Embassy Recommended',
      description: 'Regularly recommended by foreign Embassies and through word-of-mouth referrals from satisfied clients who trust our expertise.'
    },
    {
      icon: <Scale className="w-8 h-8 text-[#D4A74A]" strokeWidth={2} />,
      title: 'Fair & Just Outcomes',
      description: 'Our commitment is to ensure every client receives a fair and just outcome, handled with integrity and professionalism throughout.'
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO 
        title="Why Choose Pattaya Solicitors | Expat Solicitor Pattaya Thailand"
        description="Why choose Pattaya Solicitors: 20+ years experience, embassy recommended, English speaking lawyer Pattaya, licensed with Lawyer's Council Thailand. Trusted by expats and foreigners."
        keywords="Expat Solicitor Pattaya, English Speaking Lawyer Pattaya, Why Pattaya Solicitors, Law Firm Pattaya for Foreigners, Solicitors in Pattaya Thailand, Best Pattaya Lawyers"
        path="/why-us"
        schemaType="whyus"
      />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#000A22] via-[#001438] to-[#000A22] text-white py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <div className="inline-flex items-center gap-2 bg-[#F5E7D2] text-[#000A22] px-5 py-2.5 rounded-full font-medium text-sm">
              <Scale className="w-4 h-4 text-[#D4A74A]" />
              Why Choose Us
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold">Why Choose Pattaya Solicitors?</h1>
            <p className="text-xl text-gray-300">Expat Solicitor Pattaya | English Speaking Law Firm Thailand</p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              {reasons.map((reason, index) => (
                <Card 
                  key={index}
                  className="border-2 hover:border-[#D4A74A] transition-all duration-300 hover:shadow-xl"
                >
                  <CardContent className="p-8">
                    <div className="flex gap-6">
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 bg-[#F5E7D2] rounded-full flex items-center justify-center">
                          {reason.icon}
                        </div>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-[#000A22] mb-3">{reason.title}</h3>
                        <p className="text-gray-700 leading-relaxed">{reason.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div className="space-y-2">
              <div className="text-5xl font-bold text-[#D4A74A]">20+</div>
              <div className="text-xl text-gray-700">Years of Experience</div>
            </div>
            <div className="space-y-2">
              <div className="text-5xl font-bold text-[#D4A74A]">100%</div>
              <div className="text-xl text-gray-700">Licensed & Registered</div>
            </div>
            <div className="space-y-2">
              <div className="text-5xl font-bold text-[#D4A74A]">9+</div>
              <div className="text-xl text-gray-700">Legal Services Offered</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#000A22] to-[#001438] text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center space-y-8">
            <h2 className="text-4xl font-bold">Ready to Work With Us?</h2>
            <p className="text-xl text-gray-300">
              Experience the difference of working with Pattaya's trusted legal experts. Contact us today for a consultation.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <Button 
                size="lg"
                className="bg-[#D4A74A] hover:bg-[#C69A3E] text-[#000A22] font-semibold px-8 py-6 text-lg transition-all duration-300 hover:shadow-xl"
                asChild
              >
                <a href="tel:+6638412415">
                  <Phone className="w-5 h-5 mr-2" />
                  Call Now
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhyUsPage;
